import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { StarRating } from '../ui/star-rating';
import { GradeOpinionSlider } from './GradeOpinionSlider';
import WheelPickerSheet from '../climbs/WheelPickerSheet';
import MentionTextarea from '../comments/MentionTextarea';
import { format, subDays } from 'date-fns';
import {
  getSendDatePreference,
  setSendDateDefaultMode,
  setLastSendDate,
} from '../../utils/send-date-preference';
import type { SendType, ClimbStyleType } from '../../types/climb-log';

const SEND_TYPES: { value: SendType; labelKey: string }[] = [
  { value: 'on_sight', labelKey: 'climbLog.onSight' },
  { value: 'flash', labelKey: 'climbLog.flash' },
  { value: 'worked', labelKey: 'climbLog.worked' },
  { value: 'nd', labelKey: 'climbLog.nd' },
];

/**
 * How far past the minimum the attempts wheel scrolls. Nobody counts a hundred
 * goes at a single problem, but the wheel has to end somewhere.
 */
const ATTEMPTS_WHEEL_SPAN = 99;

const CLIMB_STYLES: { value: ClimbStyleType; labelKey: string }[] = [
  { value: 'overhang', labelKey: 'climbLog.overhang' },
  { value: 'technical', labelKey: 'climbLog.technical' },
  { value: 'strength', labelKey: 'climbLog.strength' },
  { value: 'balance', labelKey: 'climbLog.balance' },
  { value: 'coordination', labelKey: 'climbLog.coordination' },
  { value: 'endurance', labelKey: 'climbLog.endurance' },
];

export interface ClimbLogFormData {
  /** Absent when the form had no stars to give, or they were left untouched. */
  rating?: number | null;
  grade_opinion?: number | null;
  climb_style?: ClimbStyleType[];
  send_type?: SendType;
  attempts_count?: number;
  topped_at?: string;
  comment?: string;
}

/**
 * What `onSave` tells the form to do next. `'ask_rating'` keeps it open on a
 * second step that asks for the stars alone — the send form itself has none
 * for a fresh send, so nobody votes without meaning to. Anything else closes it.
 */
export type ClimbLogSaveOutcome = 'done' | 'ask_rating';

interface ClimbLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 'rate' shows stars + grade opinion; 'send' shows date + send_type + grade opinion */
  mode: 'rate' | 'send';
  /** The climb's difficulty string (e.g. "Verde", "6a") for grade opinion display */
  difficulty?: string | null;
  /** Climb type — boulders cannot be sent on_sight, so that option is hidden when 'Boulder' */
  climbType?: 'Boulder' | 'Route';
  initialValues?: {
    rating?: number | null;
    grade_opinion?: number | null;
    climb_style?: ClimbStyleType[] | null;
    send_type?: SendType | null;
    attempts_count?: number | null;
    topped_at?: string | null;
  };
  onSave: (data: ClimbLogFormData) => Promise<ClimbLogSaveOutcome | void> | ClimbLogSaveOutcome | void;
  /** Saves the stars given on the step after a send (see `ClimbLogSaveOutcome`). */
  onRate?: (rating: number) => Promise<void> | void;
  /**
   * Show the stars in send mode. Only when editing a send already logged: from
   * a list or a crag's topo, editing the send is the one way to the stars.
   */
  showRatingInSend?: boolean;
  /** Show a delete button in send mode */
  showDelete?: boolean;
  onDelete?: () => Promise<void> | void;
  /** Pre-populate comment textarea (send mode only) */
  initialComment?: string;
  /** Total existing Attempt records for this climb (send mode) — used to compute min attempts_count and the delta hint */
  existingAttemptsCount?: number;
  /** Whether a top Attempt already exists for this climb (send mode) */
  topAlreadyLogged?: boolean;
  /**
   * Earliest day the send may be dated, `yyyy-MM-dd`. Undefined means any past
   * day is fair game — which is the outdoor case, where the route was climbable
   * long before the app knew about it. See `logDateFloor`.
   */
  minDate?: string;
  /**
   * Location slug used to scope the send-date quick picker preference
   * (per-gym "Yesterday" / last-used-date shortcuts). When omitted the
   * shortcuts are hidden and the date defaults to today.
   */
  dateScope?: string | null;
}

export default function ClimbLogDialog({
  open,
  onOpenChange,
  mode,
  difficulty,
  climbType,
  initialValues,
  onSave,
  onRate,
  showRatingInSend = false,
  showDelete = false,
  onDelete,
  initialComment,
  existingAttemptsCount = 0,
  topAlreadyLogged = false,
  minDate,
  dateScope,
}: ClimbLogDialogProps) {
  const availableSendTypes = climbType === 'Boulder'
    ? SEND_TYPES.filter(({ value }) => value !== 'on_sight')
    : SEND_TYPES;
  const { t } = useTranslation();

  // Fresh send date default: editing keeps the existing date; a new send honors
  // the per-location quick-picker preference, otherwise falls back to today.
  const computeInitialToppedAt = (): string => {
    if (initialValues?.topped_at) {
      return format(new Date(initialValues.topped_at), 'yyyy-MM-dd');
    }
    if (mode === 'send' && dateScope) {
      const pref = getSendDatePreference(dateScope);
      if (pref.defaultMode === 'yesterday') {
        return format(subDays(new Date(), 1), 'yyyy-MM-dd');
      }
      if (pref.defaultMode === 'lastDate' && pref.lastDate) {
        return pref.lastDate;
      }
    }
    return format(new Date(), 'yyyy-MM-dd');
  };
  // No vote is null, so the stars start dark and ask to be given.
  const [rating, setRating] = useState<number | null>(initialValues?.rating ?? null);
  // In send mode the stars are only sent once touched, so editing a send's date
  // never writes over the vote — or casts one.
  const [ratingTouched, setRatingTouched] = useState(false);
  const [step, setStep] = useState<'form' | 'rate'>('form');
  const [stepRating, setStepRating] = useState<number | null>(null);
  const [stepSaving, setStepSaving] = useState(false);
  const stepStarsRef = useRef<HTMLDivElement>(null);
  const [gradeOpinion, setGradeOpinion] = useState<number | null>(initialValues?.grade_opinion ?? null);
  const [climbStyle, setClimbStyle] = useState<ClimbStyleType[]>(initialValues?.climb_style ?? []);
  const [sendType, setSendType] = useState<SendType | null>(initialValues?.send_type ?? null);
  const [attemptsCount, setAttemptsCount] = useState<number>(initialValues?.attempts_count ?? 1);
  const [attemptsPickerOpen, setAttemptsPickerOpen] = useState(false);
  const [toppedAt, setToppedAt] = useState<string>(computeInitialToppedAt);
  // Last send date confirmed at this location (for the reuse shortcut), refreshed on open.
  const [lastSendDate, setLastSendDateState] = useState<string | null>(
    () => (mode === 'send' ? getSendDatePreference(dateScope).lastDate : null)
  );
  const [comment, setComment] = useState<string>(initialComment ?? '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // The fewest attempts this send can honestly claim: everything already
  // recorded, plus the top itself when it is not among them.
  const minAttemptsCount = Math.max(1, existingAttemptsCount + (topAlreadyLogged ? 0 : 1));
  const newAttemptsDelta = Math.max(0, attemptsCount - existingAttemptsCount);

  // The wheel runs from that minimum to a round hundred past it, and always far
  // enough to read back a saved count that overshoots the range.
  const attemptsOptions = useMemo(() => {
    const max = Math.max(attemptsCount, minAttemptsCount + ATTEMPTS_WHEEL_SPAN);
    return Array.from({ length: max - minAttemptsCount + 1 }, (_, index) => {
      const count = minAttemptsCount + index;
      return { value: count, label: String(count) };
    });
  }, [attemptsCount, minAttemptsCount]);

  // When sendType becomes 'worked', ensure attemptsCount respects the minimum
  useEffect(() => {
    if (!open || sendType !== 'worked') return;
    if (attemptsCount < minAttemptsCount) setAttemptsCount(minAttemptsCount);
  }, [open, sendType, minAttemptsCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Picking "worked" is picking a number of goes, so the wheel comes up with
  // it — every time the button is pressed, not only the first.
  const selectSendType = (value: string) => {
    const next = (value as SendType) || null;
    // Radix clears a single-value toggle when its active item is tapped again;
    // on "worked" that tap means "let me change the count", not "undo".
    if (!next && sendType === 'worked') {
      setAttemptsPickerOpen(true);
      return;
    }
    setSendType(next);
    if (next === 'worked') {
      // Clamped here rather than left to the effect above, so the wheel opens
      // on a count its own options actually contain.
      if (attemptsCount < minAttemptsCount) setAttemptsCount(minAttemptsCount);
      setAttemptsPickerOpen(true);
    }
  };

  // Back to the form on every opening, before the browser paints, so a dialog
  // reopened after the stars step — or closed while its save was in flight —
  // never flashes the step.
  useLayoutEffect(() => {
    if (open) {
      setStep('form');
      setStepRating(null);
    }
  }, [open]);

  // The button that had the focus went with the form: it lands on the stars.
  useEffect(() => {
    if (step === 'rate') stepStarsRef.current?.querySelector('button')?.focus();
  }, [step]);

  // Sync initial values when dialog opens
  useEffect(() => {
    if (open) {
      setRating(initialValues?.rating ?? null);
      setRatingTouched(false);
      setGradeOpinion(initialValues?.grade_opinion ?? null);
      setClimbStyle(initialValues?.climb_style ?? []);
      setSendType(initialValues?.send_type ?? null);
      setAttemptsCount(initialValues?.attempts_count ?? 1);
      setAttemptsPickerOpen(false);
      setToppedAt(computeInitialToppedAt());
      setLastSendDateState(mode === 'send' ? getSendDatePreference(dateScope).lastDate : null);
      setComment(initialComment ?? '');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // The send's existing comment is fetched as the form opens, so it can land a
  // moment after this: taken while the box is still empty, never over something
  // already typed.
  useEffect(() => {
    if (!open || !initialComment) return;
    setComment((current) => (current === '' ? initialComment : current));
  }, [open, initialComment]);

  const changeRating = (value: number | null) => {
    setRating(value);
    setRatingTouched(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let outcome: ClimbLogSaveOutcome | void;
      if (mode === 'rate') {
        outcome = await onSave({
          rating,
          grade_opinion: gradeOpinion,
          climb_style: climbStyle,
        });
      } else {
        const data: ClimbLogFormData = {
          climb_style: climbStyle,
          grade_opinion: gradeOpinion,
          topped_at: toppedAt,
          comment: comment.trim() || undefined,
        };
        if (showRatingInSend && ratingTouched) data.rating = rating;
        if (sendType) {
          data.send_type = sendType;
          if (sendType === 'worked') {
            data.attempts_count = attemptsCount;
          }
        }
        outcome = await onSave(data);
        // Remember the confirmed date so it can be reused for the next send here.
        if (dateScope) setLastSendDate(dateScope, toppedAt);
      }
      if (outcome === 'ask_rating' && onRate) {
        setStep('rate');
      } else {
        onOpenChange(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStepRate = async () => {
    if (stepRating == null || !onRate) return;
    setStepSaving(true);
    try {
      await onRate(stepRating);
      onOpenChange(false);
    } finally {
      setStepSaving(false);
    }
  };

  const starLabel = (count: number) => t('climbLog.starLabel', { count });

  // Quick-pick a send date and make that choice the default for the next entries.
  const applyQuickDate = (mode: 'yesterday' | 'lastDate') => {
    const value = mode === 'yesterday'
      ? format(subDays(new Date(), 1), 'yyyy-MM-dd')
      : lastSendDate;
    if (!value) return;
    setToppedAt(value);
    if (dateScope) setSendDateDefaultMode(dateScope, mode);
  };

  const yesterdayValue = format(subDays(new Date(), 1), 'yyyy-MM-dd');

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
      onOpenChange(false);
    } finally {
      setDeleting(false);
    }
  };

  if (step === 'rate') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('climbLog.rateAfterSendTitle')}</DialogTitle>
            <DialogDescription>{t('climbLog.rateAfterSendDescription')}</DialogDescription>
          </DialogHeader>

          <div ref={stepStarsRef} className="flex justify-center py-2">
            <StarRating
              value={stepRating}
              onChange={setStepRating}
              disabled={stepSaving}
              starLabel={starLabel}
              className="[&_svg]:h-9 [&_svg]:w-9"
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={stepSaving}>
              {t('climbLog.notNow')}
            </Button>
            <Button onClick={handleStepRate} disabled={stepRating == null || stepSaving}>
              {t('climbLog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'rate' ? t('climbLog.rateTitle') : t('climbLog.sendTitle')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'rate' ? t('climbLog.rateDescription') : t('climbLog.sendDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {mode === 'rate' && (
            <>
              <div className="space-y-2">
                <Label>{t('climbLog.rating')}</Label>
                <StarRating value={rating} onChange={setRating} starLabel={starLabel} />
              </div>

              <GradeOpinionSlider
                difficulty={difficulty}
                value={gradeOpinion}
                onChange={setGradeOpinion}
              />

              <div className="space-y-2">
                <Label>{t('climbLog.climbStyle')}</Label>
                <ToggleGroup
                  type="multiple"
                  value={climbStyle}
                  onValueChange={(value) => setClimbStyle(value as ClimbStyleType[])}
                  className="flex flex-wrap gap-1"
                >
                  {CLIMB_STYLES.map(({ value, labelKey }) => (
                    <ToggleGroupItem
                      key={value}
                      value={value}
                      className="text-xs border border-input rounded-md px-2 py-1 data-[state=on]:bg-blue-600 data-[state=on]:text-white"
                    >
                      {t(labelKey)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </>
          )}

          {mode === 'send' && (
            <>
              {/* Topped at date + quick shortcuts */}
              <div className="space-y-2">
                <Label htmlFor="topped-at">{t('climbLog.toppedAt')}</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="topped-at"
                    type="date"
                    value={toppedAt}
                    min={minDate}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    onChange={(e) => setToppedAt(e.target.value)}
                    className="w-40"
                  />
                  {dateScope && (
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant={toppedAt === yesterdayValue ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => applyQuickDate('yesterday')}
                      >
                        {t('climbLog.yesterday')}
                      </Button>
                      {lastSendDate && lastSendDate !== yesterdayValue && (
                        <Button
                          type="button"
                          variant={toppedAt === lastSendDate ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => applyQuickDate('lastDate')}
                          title={t('climbLog.lastDateTooltip')}
                        >
                          {format(new Date(lastSendDate), 'dd/MM')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Send Type */}
              <div className="space-y-2">
                <Label>{t('climbLog.sendType')}</Label>
                <ToggleGroup
                  type="single"
                  value={sendType || ''}
                  onValueChange={selectSendType}
                  className="flex flex-wrap gap-1"
                >
                  {availableSendTypes.map(({ value, labelKey }) => (
                    <ToggleGroupItem
                      key={value}
                      value={value}
                      className="text-xs border border-input rounded-md px-2 py-1 data-[state=on]:bg-green-600 data-[state=on]:text-white"
                    >
                      {/* A worked send is only half told without the count, so
                          the button reads it back and offers it up again. */}
                      {value === 'worked' && sendType === 'worked'
                        ? t('climbLog.workedWithCount', { attempts: attemptsCount })
                        : t(labelKey)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>

                {sendType === 'worked' && existingAttemptsCount > 0 && newAttemptsDelta > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('climbLog.newAttemptsHint', { count: newAttemptsDelta })}
                  </p>
                )}

                <WheelPickerSheet
                  open={attemptsPickerOpen}
                  onOpenChange={setAttemptsPickerOpen}
                  label={t('climbLog.attemptsCount')}
                  value={attemptsCount}
                  options={attemptsOptions}
                  onChange={setAttemptsCount}
                />
              </div>

              <GradeOpinionSlider
                difficulty={difficulty}
                value={gradeOpinion}
                onChange={setGradeOpinion}
              />

              <div className="space-y-2">
                <Label>{t('comments.addComment')}</Label>
                <MentionTextarea
                  value={comment}
                  onChange={setComment}
                  disabled={saving}
                  minRows={2}
                />
              </div>

              {/* A fresh send has no stars: they are asked for on their own
                  once it is saved. Editing one keeps them, see showRatingInSend. */}
              {showRatingInSend && (
                <div className="space-y-2">
                  <Label>{t('climbLog.rating')}</Label>
                  <StarRating value={rating} onChange={changeRating} starLabel={starLabel} />
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('climbLog.climbStyle')}</Label>
                <ToggleGroup
                  type="multiple"
                  value={climbStyle}
                  onValueChange={(value) => setClimbStyle(value as ClimbStyleType[])}
                  className="flex flex-wrap gap-1"
                >
                  {CLIMB_STYLES.map(({ value, labelKey }) => (
                    <ToggleGroupItem
                      key={value}
                      value={value}
                      className="text-xs border border-input rounded-md px-2 py-1 data-[state=on]:bg-blue-600 data-[state=on]:text-white"
                    >
                      {t(labelKey)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          {showDelete && mode === 'send' && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="mr-auto"
            >
              {t('climbLog.delete')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving || deleting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || deleting}>
            {t('climbLog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
